import {
    ActionTypeEnum,
    EStatus,
    IJson,
    IJsonScenario,
    IPayloadScenario,
    IRootState,
    TInitWidgetState,
    TTriggerWidgetAction,
    ValidationErrors,
} from '@libs/types';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { CFAccessDeniedError, fetchJson, initWidgetState, isLoadingAction } from '../../../foundation';
import { resolveScenarioTheme } from '../../service/ThemeService';
import { setStatus, setTheme } from '../scenario.slice';

const getPayload = (scenario: IJsonScenario): IPayloadScenario => {
    const { mutableWidgetsMap, bottomSheetMap, modalWindowMap, internalIdWidgets, systemActions, submitWidget, rootWidgets } = initWidgetState(
        scenario.pages,
    ) as TInitWidgetState;
    return {
        scenarioId: scenario?.scenarioId,
        currentPageId: scenario?.pages?.[0]?.id,
        configuration: scenario?.configuration ?? {},
        bottomSheetMap,
        mutableWidgetsMap,
        submitWidget,
        internalIdWidgets,
        modalWindowMap,
        systemActions,
        rootWidgets,
    };
};

const applyScenarioTheme = async (scenario: IJsonScenario, dispatch: (action: unknown) => void) => {
    const theme = await resolveScenarioTheme(scenario);
    if (theme) dispatch(setTheme(theme));
};

export const fetchScenario = createAsyncThunk<
    IPayloadScenario | undefined,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchScenario', async ({ action, widgetUid, isShowLoader, keepalive, playerId, initPayerOptions, scenarioId, currentPageId }, thunkAPI) => {
    try {
        const { scenario } = thunkAPI.getState();
        let json = await fetchJson(
            action,
            isShowLoader || isLoadingAction(action),
            widgetUid,
            scenario.currentPageId,
            scenario.mutableWidgetsMap,
            (status: EStatus) => {
                thunkAPI.dispatch(setStatus(status));
            },
            keepalive,
            playerId,
            initPayerOptions,
        );

        const isDelayedProductAction =
            (action?.type === ActionTypeEnum.Product || action?.type === ActionTypeEnum.ProductJump) && action?.delay && action.delay > 0;

        if (!isDelayedProductAction) {
            await applyScenarioTheme(json.scenario, thunkAPI.dispatch);
            return getPayload(json.scenario);
        }

        const isSamePage = json.scenario.scenarioId === scenarioId && json.scenario.pages[0]?.id === currentPageId;

        if (isSamePage) {
            json = await fetchJson(
                undefined,
                true,
                widgetUid,
                scenario.currentPageId,
                scenario.mutableWidgetsMap,
                (status: EStatus) => {
                    thunkAPI.dispatch(setStatus(status));
                },
                keepalive,
                playerId,
                initPayerOptions,
            );
        }
        await applyScenarioTheme(json.scenario, thunkAPI.dispatch);
        return getPayload(json.scenario);
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        console.error(`fetchScenario: ${widgetUid ?? ''}; ${action?.name ?? ''}  ${e}`);
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('fetchScenario: request was aborted by core');
        } else if (isCFAccessDeniedError) {
            console.log('fetchScenario: CFAccessDeniedError');
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            // Keep the action (and the widget it came from) in the error state so the UI can
            // replay the exact request — see the offline popup retry in the consumer WidgetWrapper
            // and the captcha replay in ChallengeIframe. `isOffline` is captured at failure time so
            // the UI can tell a lost connection from a real backend error even after the network is back.
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
                widgetUid,
                actionForRepeat: action,
                isOffline: !navigator.onLine,
            });
        }
    }
});

export const fetchJsonAsync = createAsyncThunk<
    IJson,
    TTriggerWidgetAction,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('fetchJsonAsync', async ({ action, isShowLoader, keepalive, widgetUid, playerId }, thunkAPI) => {
    try {
        const { scenario } = thunkAPI.getState();
        return await fetchJson(
            action,
            isShowLoader ?? isLoadingAction(action),
            widgetUid,
            scenario.currentPageId,
            scenario.mutableWidgetsMap,
            (status: EStatus) => {
                thunkAPI.dispatch(setStatus(status));
            },
            keepalive,
            playerId,
        );
    } catch (e: unknown) {
        const isCFAccessDeniedError = e instanceof CFAccessDeniedError;
        console.error(`fetchJsonAsync: ${widgetUid ?? ''}; ${action?.name ?? ''} ${e}`);
        if (e instanceof Error && e.name === 'AbortError') {
            console.log('fetchJsonAsync: request was aborted by core');
            throw e;
        } else if (isCFAccessDeniedError) {
            console.log('fetchJsonAsync: CFAccessDeniedError');
            return thunkAPI.rejectWithValue({
                errorMessage: 'CF started',
                isForbidden: true,
                captchaBody: (e as CFAccessDeniedError).getBody(),
                widgetUid: widgetUid,
                actionForRepeat: action,
            });
        } else {
            return thunkAPI.rejectWithValue({
                errorMessage: 'Response do not have any scenario!',
                widgetUid,
                actionForRepeat: action,
                isOffline: !navigator.onLine,
            });
        }
    }
});

export const updateScenario = createAsyncThunk<
    IPayloadScenario | undefined,
    IJson,
    {
        state: IRootState;
        rejectValue: ValidationErrors;
    }
>('updateScenario', async (json, thunkAPI) => {
    await applyScenarioTheme(json.scenario, thunkAPI.dispatch);
    return getPayload(json.scenario);
});
